'use client';

import React, { FunctionComponent, useMemo } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { ReactComponent as CopyLinkIcon } from '../../../static/product_image/copylink.svg';
import CloseIcon from '@mui/icons-material/Close';
import { useGetCompareQuery } from '../../../state/compareSlice';
import { ComparableParameterGroup } from '../../../schemas/ComparableParameterGroup';
import CompareInfoTable from './CompareInfoTable';
import { CompareItem } from './CompareBar';
import { ProductsContent } from '../../../graphql/pimcore/modules/siteContent/types/ProductsContent';
import { useLocale } from '../../../contexts/LocaleProvider';
import { formatCountIcuString } from '../../../utils/formatCount';
import { createPortal } from 'react-dom';

interface CompareInfoProps {
    handleClose: () => void;
    compareItems: CompareItem[];
    onClearCompareItem: (basePn: string) => void;
    contentTree: ProductsContent;
}

const CompareInfo: FunctionComponent<CompareInfoProps> = (props) => {
    const { locale } = useLocale();
    const handleCopyLink = () => {
        const currentUrl = window.location.href;
        navigator.clipboard.writeText(currentUrl);
    };

    // 根據 compareItems 取得各項的部件編號 (如果是 BasePartParameterValue 則取 basePn，否則取 uniquePn)
    const pns: string[] = props.compareItems.map((item) => {
        if ('basePn' in item && typeof item.basePn === 'string') {
            return item.basePn;
        }
        if ('uniquePn' in item && typeof item.uniquePn === 'string') {
            return item.uniquePn;
        }
        return '';
    });

    // 呼叫 API query，回傳型別為 CompareResponse
    const { data: compareResponse, error, isLoading } = useGetCompareQuery(pns);

    // 3) 用 useMemo 在 compareResponse 改變時重新計算 Map
    const partParamMap = useMemo(() => {
        // 若還沒拿到 compareResponse 或其內部結構不存在，就回傳空 Map
        if (
            !compareResponse ||
            !compareResponse.comparisons ||
            !compareResponse.comparableParameterGroups
        ) {
            return new Map<string, ComparableParameterGroup[]>();
        }

        const { comparisons, comparableParameterGroups } = compareResponse;
        const map = new Map<string, ComparableParameterGroup[]>();

        comparisons.forEach((partNumber, idx) => {
            map.set(partNumber, comparableParameterGroups[idx] ?? []);
        });

        return map;
    }, [compareResponse]);

    return createPortal(
        <Box
            sx={{
                position: 'fixed',
                top: '5vh',
                left: '5vw',
                width: '90vw',
                height: '90vh',
                backgroundColor: '#F2F2F2',
                zIndex: 9999,
                p: 3,
                overflow: 'auto',
                boxShadow: '0px -4px 4px 0px #00000040',
            }}
        >
            {/* 頂部區塊：左右兩邊分開對齊 */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    paddingBottom: '1rem',
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center', // 水平置中
                        alignItems: 'center', // 垂直置中
                    }}
                    data-testid={'compare-parts-length'}
                >
                    <Typography
                        sx={{
                            fontFamily: 'Poppins',
                            fontWeight: 400,
                            fontSize: '1.5rem',
                            lineHeight: '117%',
                        }}
                    >
                        {formatCountIcuString(
                            props.contentTree.comparison.result.pageTitle.content,
                            props.compareItems.length,
                            locale
                        )}
                    </Typography>
                </Box>

                {/* 右側按鈕和關閉圖示 */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Button
                        variant="contained"
                        onClick={handleCopyLink}
                        startIcon={<CopyLinkIcon />}
                        sx={{
                            backgroundColor: '#B75332',
                            transition: 'none',
                            '&:hover': {
                                background: 'linear-gradient(0deg, #B75332 0%, #E58869 100%)',
                                transition: 'none',
                            },
                            color: '#fff',
                            fontFamily: 'Poppins',
                            fontWeight: 400,
                            fontSize: '1rem',
                            lineHeight: '117%',
                            padding: '10px 20px',
                            borderRadius: '5px',
                        }}
                    >
                        {props.contentTree.comparison.result.copyComparisonLinkButtonLabel.content}
                    </Button>
                    <CloseIcon
                        onClick={props.handleClose}
                        sx={{
                            color: '#B75332',
                            cursor: 'pointer',
                            width: '40px',
                            height: '40px',
                        }}
                    />
                </Box>
            </Box>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    backgroundColor: 'white',
                }}
                data-testid={'compare-detail'}
            >
                <CompareInfoTable
                    onClearCompareItem={props.onClearCompareItem}
                    partParamMap={partParamMap}
                    compareItems={props.compareItems}
                    contentTree={props.contentTree}
                />
            </Box>
        </Box>,
        document.body
    );
};

export default CompareInfo;
